import React, { useState, useEffect, useCallback } from "react";
import {
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CTabContent,
  CTab,
  CTabs,
  CTabList,
  CTabPanel,
} from "@coreui/react-pro";
import { FieldArray } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { getQuoteRequest } from "../../utils/backend";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./uploadQuotation.css";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";

const ViewRequestQuotation = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    insurance: "",
    plan_name: "",
    policy_wording: "",
    product_disclosure_sheet: "",
    premium_price: "",
    sum_insured: "",
    ncd_rate: "",
    add_ons: [{ name: "", price: "" }],
    plan_benefits: [""],
  });
  const [tabs, setTabs] = useState([
    { key: "tab1", label: "Tab 1", values: { ...initialValues } },
  ]);
  const [order, setOrder] = useState({});
  const id = useParams();

  const ownershipTypeMapping = {
    I: "Individual",
    C: "Company",
  };

  const fetchData = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const response = await getQuoteRequest(id);
      console.log(response);
      if (response.success) {
        const data = response.data;
        const quotations = data?.motor_insurance_quotations;

        if (quotations && quotations.length > 0) {
          const updatedTabs = quotations.map((quotation, index) => {
            const insuranceProvider = quotation.insurance_provider;

            const addOns = quotation.add_ons.map((addOn) => ({
              name: addOn.add_on_name,
              price: addOn.add_on_price,
            }));

            const planBenefits = quotation.plan_benefits.map(
              (planBenefit) => planBenefit.plan_benefit
            );

            return {
              key: `tab${index + 1}`,
              label: insuranceProvider,
              values: {
                ...initialValues,
                insurance: insuranceProvider,
                plan_name: quotation.plan_name,
                policy_wording: quotation.policy_wording_document,
                product_disclosure_sheet: quotation.product_disclosure_sheet,
                premium_price: quotation.price,
                sum_insured: quotation.sum_insured,
                ncd_rate: quotation.ncd_rate,
                add_ons: addOns,
                plan_benefits: planBenefits,
              },
            };
          });

          setTabs(updatedTabs);
        }

        setOrder(data);
      } else {
        toast.error("Failed to fetch order details");
        setOrder({});
      }
    } catch (error) {
      toast.error("Error fetching data");
      setOrder({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <div className="card border-light">
          <div className="card-body p-4">
            <label className="mb-4">Personal Information Details</label>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">User ID:</label>
                <p>
                  <b>{order.user_details?.id || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Owner Name:</label>
                <p>
                  <b>{order.quote_request?.policy_holder_name || "N/A"}</b>
                </p>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">ID Type:</label>
                <p>
                  <b>{order.quote_request?.id_type || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">ID No:</label>
                <p>
                  <b>{order.quote_request?.id_no || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Postcode:</label>
                <p>
                  <b>{order.quote_request?.postcode || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Vehicle Type:</label>
                <p>
                  <b>
                    {order.quote_request?.veh_type?.replace(/^./, (c) =>
                      c.toUpperCase()
                    ) || "N/A"}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Email Address:</label>
                <p>
                  <b>
                    {order.quote_request?.email ||
                      (order.user_details?.email === "jpjredirect@ansa.my"
                        ? "N/A"
                        : order.user_details?.email || "N/A")}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Contact Number:</label>
                <p>
                  <b>
                    {order.quote_request?.hphoneno ||
                      (order.user_details?.email === "jpjredirect@ansa.my"
                        ? "N/A"
                        : order.user_details?.phone_number || "N/A")}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Marital Status:</label>
                <p>
                  <b>
                    {(order.quote_request?.marital_code === "M"
                      ? "Married"
                      : order.quote_request?.marital_code === "S"
                      ? "Single"
                      : order.quote_request?.marital_code === "D"
                      ? "Divorced"
                      : order.quote_request?.marital_code === "W"
                      ? "Widowed"
                      : "Others") || "N/A"}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Company Name:</label>
                <p>
                  <b>{order.quote_request?.company_name || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Vehicle No.:</label>
                <p>
                  <b>{order.quote_request?.veh_reg_no || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Created At:</label>
                <p>
                  <b>
                    {order.created_at
                      ? formatDateTime(order.created_at)
                      : "N/A"}
                  </b>
                </p>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Request Status:</label>
                <p>
                  <CDropdown>
                    <CDropdownToggle
                      color="secondary"
                      variant="outline"
                      style={{
                        backgroundColor:
                          order.order_status === "Pending Quotation"
                            ? "#A9A9A9" // Orange
                            : order.order_status === "Quotation is Ready"
                            ? "#66BB6A" // Green
                            : order.order_status === "Closed"
                            ? "#000000" // Black
                            : "#FFFFFF", // Default
                        color: "#fff",
                        border: "none",
                        width: "160px",
                        fontSize: "14px",
                        borderRadius: "20px",
                        fontWeight: "500",
                      }}
                    >
                      {order.order_status
                        ? order.order_status.charAt(0).toUpperCase() +
                          order.order_status.slice(1)
                        : "N/A"}
                    </CDropdownToggle>
                    <CDropdownMenu>
                      <CDropdownItem>Pending Quotation</CDropdownItem>
                      <CDropdownItem>Quotation is Ready</CDropdownItem>
                      <CDropdownItem>Closed</CDropdownItem>
                    </CDropdownMenu>
                  </CDropdown>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">NCD:</label>
                <p>
                  <b>
                    {order.quote_request?.ncd_percent !== null
                      ? Math.floor(order.quote_request?.ncd_percent) + "%"
                      : "N/A"}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Preferred Insurances:</label>
                <p>
                  <b>
                    {Array.isArray(order.quote_preferred_insurers) &&
                    order.quote_preferred_insurers.length > 0
                      ? order.quote_preferred_insurers
                          .map((item) => item.insurer_name)
                          .join(", ")
                      : "N/A"}
                  </b>
                </p>
              </div>
            </div>

            <label className="mb-1s d-flex justify-content-between align-items-center">
              Quotation Details
            </label>

            <CTabs activeItemKey={tabs[0]?.key}>
              <CTabList variant="underline" className="mb-3">
                {tabs.map((tab) => (
                  <div key={tab.key} className="d-flex align-items-center">
                    <CTab itemKey={tab.key}>{tab.label}</CTab>
                  </div>
                ))}
              </CTabList>

              <CTabContent>
                {tabs.map((tab, index) => (
                  <CTabPanel
                    key={tab.key}
                    itemKey={tab.key}
                    style={{ margin: "", borderRadius: "10px" }}
                  >
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Insurance Name:</label>
                        <p>
                          <b>{tab.values.insurance || "N/A"}</b>
                        </p>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Plan Name:</label>
                        <p>
                          <b>{tab.values.plan_name || "N/A"}</b>
                        </p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Policy Wording Document (URL):
                        </label>
                        <p>
                          <b>{tab.values.policy_wording || "N/A"}</b>
                        </p>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Product Disclosure Sheet (URL):
                        </label>
                        <p>
                          <b>{tab.values.product_disclosure_sheet || "N/A"}</b>
                        </p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Basic Premium:</label>
                        <p>
                          <b>{tab.values.premium_price || "N/A"}</b>
                        </p>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Sum Insured:</label>
                        <p>
                          <b>{tab.values.sum_insured || "N/A"}</b>
                        </p>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          No Claim Discount (NCD) Rate:
                        </label>
                        <p>
                          <b>{tab.values.ncd_rate || "N/A"}</b>
                        </p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <label htmlFor="plan_benefits" className="form-label">
                          Plan Benefits
                        </label>

                        {tab.values.plan_benefits?.map((benefit, index) => (
                          <div key={index} className="mb-2">
                            <div className="position-relative">
                              <p>
                                <b>{benefit || "N/A"}</b>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <label htmlFor="add_ons" className="form-label">
                          Add-ons
                        </label>

                        {tab.values.add_ons.map((addOn, index) => (
                          <div key={index} className="mb-2">
                            <div className="d-flex">
                              <div
                                className="flex-grow-2 me-2 position-relative"
                                style={{ flex: "2 1 0" }}
                              >
                                <p>
                                  <b>
                                    {addOn.name || "N/A"} -{" "}
                                    {addOn.price || "N/A"}
                                  </b>
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CTabPanel>
                ))}
              </CTabContent>

              <div className="d-flex justify-content-end mt-3">
                <button
                  type="button"
                  className="btn me-3 ansa-button-secondary"
                  onClick={() => navigate("/requests")}
                >
                  Back
                </button>
              </div>
            </CTabs>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewRequestQuotation;
